import {
  formatDateOnly,
  isAfter,
  isBefore,
  startOfMonth,
} from '@myre/common-dates';
import { LangLocale, languages } from '@myre/common-i18n';
import { uppercaseFirst } from '@myre/common-primitives';
import { LANGLOCALE } from '@myre/ui-primitive-core';

import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  inject,
  Input,
  Output,
  type OnChanges,
  type OnInit,
  type SimpleChanges,
} from '@angular/core';
import { MAT_DATE_FORMATS } from '@angular/material/core';
import { MatCalendarCell, MatMonthView } from '@angular/material/datepicker';
import {
  addDays,
  endOfMonth,
  format,
  getDate,
  getMonth,
  getYear,
  subDays,
} from 'date-fns';

import { MyreCalendarBodyComponent } from '../myre-calendar-body/myre-calendar-body.component';
import { type CalendarEvent } from '../myre-calendar/myre-calendar.interface';
import { MyreEventCalendarBodyComponent } from '../myre-event-calendar-body/myre-event-calendar-body.component';

import {
  MONTH_VIEW_FORMATS,
  type EventCalendarDay,
} from './myre-month-view.interface';

@Component({
  selector: 'myre-month-view',
  templateUrl: './myre-month-view.component.html',
  styleUrls: ['./myre-month-view.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: MAT_DATE_FORMATS,
      useValue: MONTH_VIEW_FORMATS,
    },
  ],
  imports: [MyreCalendarBodyComponent, MyreEventCalendarBodyComponent],
})
export class MyreMonthViewComponent
  extends MatMonthView<Date>
  implements OnInit, OnChanges
{
  private readonly _langLocale = inject<LangLocale>(LANGLOCALE);

  @Input()
  public selectedDates: Date[] = [];

  @Input()
  public set numberOfWeeksToAdd(numberOfWeeksToAdd: number) {
    this._numberOfWeeksToAdd = numberOfWeeksToAdd;
  }

  @Input()
  public eventCalendar: boolean = false;

  @Input()
  public events: Map<string, CalendarEvent[]>;

  @Input()
  public canEdit: boolean = false;

  // Replace original angular setter and getter for activeDate:
  // corresponding to the date which sets the aimed displayed month
  @Input()
  public override set activeDate(value: Date) {
    const oldActiveDate = (this as any)._activeDate;
    (this as any)._activeDate = value || new Date();

    // Original component is _activeDate: D, that's why we need to declare as any here to use Date activeDate
    if (!this._sameMonthAndYear(oldActiveDate, (this as any)._activeDate)) {
      this._init();
    }
  }

  public override get activeDate(): Date {
    return (this as any)._activeDate;
  }

  public monthLabel: string;
  public selectedDatesInMonth: number[] = [];
  public weekdays: string[];

  @Output()
  public override selectedChange = new EventEmitter<Date>();

  @Output()
  public onEventCreated = new EventEmitter<Date>();

  @Output()
  public onEventUpdated = new EventEmitter<CalendarEvent>();

  private _numberOfWeeksToAdd: number = 0;

  public _eventWeeks: EventCalendarDay[][] = [];

  public get _weeksValue(): EventCalendarDay[][] {
    return this.eventCalendar
      ? this._eventWeeks
      : (this._weeks() as unknown as EventCalendarDay[][]);
  }

  constructor() {
    super();
  }

  public ngOnInit(): void {
    this._dateAdapter.setLocale(this._langLocale);
  }

  public override ngOnChanges(changes: SimpleChanges): void {
    const monthDate = this.activeDate;

    this.weekdays = this._getWeekdays();
    this.monthLabel = uppercaseFirst(
      format(monthDate, 'MMMM', {
        locale: languages[this._langLocale].dateFnsLocale,
      }),
    );

    if (
      monthDate &&
      this.minDate &&
      isAfter(this.minDate, endOfMonth(monthDate))
    ) {
      this.dateFilter = () => false;
      this.minDate = undefined;
    }

    if (
      monthDate &&
      this.maxDate &&
      isBefore(this.maxDate, startOfMonth(monthDate))
    ) {
      this.maxDate = undefined;
      this.dateFilter = () => false;
    }

    if (this.selectedDates && monthDate) {
      this.selectedDatesInMonth = this.selectedDates
        .map(d => this._getDateIfInCurrentMonth(d))
        .filter(d => d !== null);
    }

    if (
      this.events &&
      this.eventCalendar &&
      this._weeks() &&
      changes['events']
    ) {
      this._eventWeeks = this._updateWeeksWithEvents();
    }
  }

  public override _init(): void {
    super._init();

    this._completeWithLastMonthDays();
    this._completeWithNextMonthDays();

    if (this.eventCalendar) {
      this._eventWeeks = this._updateWeeksWithEvents();
    }
  }

  public onEventCreatedEmit(date: Date): void {
    this.onEventCreated.emit(date);
  }

  public onEventUpdatedEmit(event: CalendarEvent): void {
    this.onEventUpdated.emit(event);
  }

  private _getDateIfInCurrentMonth(date: Date): number {
    return this._sameMonthAndYear(date, this.activeDate) ? getDate(date) : null;
  }

  private _sameMonthAndYear(d1: Date, d2: Date): boolean {
    return !!(
      d1 &&
      d2 &&
      getMonth(d1) === getMonth(d2) &&
      getYear(d1) === getYear(d2)
    );
  }

  private _completeWithLastMonthDays(): void {
    const weeks = this._weeks();

    for (let i = 0; i < this._firstWeekOffset(); i++) {
      const date = subDays(startOfMonth(this.activeDate), i + 1);

      weeks[0].unshift(this._createMatCalendarCellDisabled(date));
    }
    this._weeks.set([...weeks]);
  }

  private _completeWithNextMonthDays(): void {
    const weeks = this._weeks();
    const lastWeek = weeks[weeks.length - 1];
    const lastWeekOffset = 7 - lastWeek.length;

    let date = endOfMonth(this.activeDate);

    for (let i = 0; i < lastWeekOffset + 7 * this._numberOfWeeksToAdd; i++) {
      if (weeks[weeks.length - 1].length === 7) {
        weeks.push([]);
      }

      date = addDays(date, 1);

      weeks[weeks.length - 1].push(this._createMatCalendarCellDisabled(date));
    }
    this._weeks.set([...weeks]);
  }

  private _createMatCalendarCellDisabled(date: Date): EventCalendarDay {
    const cellClasses = this.dateClass
      ? this.dateClass(date, 'month')
      : 'completion-cell';

    return this._getEventCalendarDay(
      new MatCalendarCell(
        -1,
        format(date, 'd'),
        '',
        false,
        cellClasses,
        date.getTime(),
        date,
      ),
    );
  }

  private _updateWeeksWithEvents(): EventCalendarDay[][] {
    return this._weeks().map(week =>
      week.map(day => this._getEventCalendarDay(day)),
    );
  }

  private _getEventCalendarDay(day: MatCalendarCell): EventCalendarDay {
    const dayKey = formatDateOnly(new Date(day.compareValue));
    const currentEvents = this.events ? this.events.get(dayKey) : [];
    const events = currentEvents && currentEvents.length ? currentEvents : [];

    const eventsToShow = events ? events.slice(0, 3) : [];
    const leftEventsNumber = events.length - 3;

    const dayWithMonth = day.rawValue
      ? format(day.rawValue, 'MMMM d', {
          locale: languages[this._langLocale].dateFnsLocale,
        })
      : '';

    return {
      ...day,
      dayWithMonth,
      events,
      eventsToShow,
      leftEventsNumber,
    };
  }

  private _getWeekdays(): string[] {
    const weekDaysFormat = this.eventCalendar ? 'long' : 'narrow';
    const firstDayOfWeek = this._dateAdapter.getFirstDayOfWeek();
    const weekdays = this._dateAdapter
      .getDayOfWeekNames(weekDaysFormat)
      .map(nw => uppercaseFirst(nw));

    return weekdays
      .slice(firstDayOfWeek)
      .concat(weekdays.slice(0, firstDayOfWeek));
  }
}
